import React, { memo } from 'react';
import { Helmet } from 'react-helmet-async';
import { areEqual } from 'react-window';
import { ELASTIC_MAX_PAGES, ROOT_BASE_URL } from '@/utils/constants';

import { SearchMetaProps, SearchProps } from './SearchMeta.interface';

const stringListsToTuples = ([k, l]: [string, string[]]) => (l || []).map((v) => [k, v]);
const entriesIntoTuples = (acc: [string, any][], [k, v]: [string, any]) => {
  switch (k) {
    case 'keywords':
      if (Array.isArray(v)) return [...acc, ...v.flatMap(stringListsToTuples)];
      return v ? [...acc, [k, v]] : acc;
    case 'filters':
      return [...acc, ...v.flatMap(stringListsToTuples)];
    default:
      return [...acc, [k, v]];
  }
};

const Q_SORT: Record<string, number> = {
  q: 1,
  keywords: 2,
  category_type: 3,
  filters: 4,
  searchBy: 5,
  sortType: 6,
  sortBy: 7,
  size: 8,
  page: 9,
};
const entriesByName = ([n1]: [string, any], [n2]: [string, any]) =>
  (Q_SORT[n1] || 4) - (Q_SORT[n2] || 4);

const paramsToEntries = ([k, v]: [string, any]): [string, any] => {
  if (k === 'filters') return [k, Object.entries(v)];
  return [k, v];
};

const Q_EXCLUSIONS = new Set(['page', 'needsPush']);
const exclusionsFromParams = ([p]: [string, any]) => !Q_EXCLUSIONS.has(p);

const getParamTuples = (params: SearchProps) =>
  Object.entries(params)
    .filter(exclusionsFromParams)
    .map(paramsToEntries)
    .sort(entriesByName)
    .reduce(entriesIntoTuples, []);

const buildQueryStr = (acc: string, [k, v]: (string | number)[], i: number) =>
  `${acc}${i ? '&' : '?'}${k}=${v}`;

const SearchMeta = ({ paging, query, config = {} }: SearchMetaProps) => {
  const base = `${ROOT_BASE_URL}${window.location.pathname}`;
  if (query?.filters?.id)
    return (
      <>
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore */}
        <Helmet>
          <meta name="robots" content="index, follow" />
          <link rel="canonical" href={`${base}?id=${query.filters.id}`} />
        </Helmet>
      </>
    );

  const { currentPage, totalPages } = paging || {};
  const isPreceded = currentPage > 1;
  const isSucceeded = currentPage < (totalPages || ELASTIC_MAX_PAGES);

  const { canonParams = { size: 100 }, isLanding } = config;
  const cList = getParamTuples(canonParams);
  const { q, keywords, category_type: catType, size = 0 } = query;
  const thisList =
    isPreceded && +size === canonParams.size && !q && !keywords && !catType
      ? [...cList, ['page', currentPage]]
      : [...cList];

  const qList = getParamTuples(query);
  const prevList = currentPage === 2 ? [...qList] : [...qList, ['page', currentPage - 1]];
  const nextList = [...qList, ['page', currentPage + 1]];

  return (
    <>
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore */}
      <Helmet>
        {!isLanding && <meta name="robots" content="noindex, follow" />}
        {!isLanding && (
          <link rel="canonical" href={`${base}${thisList.reduce(buildQueryStr, '')}`} />
        )}
        {isPreceded && <link rel="prev" href={`${base}${prevList.reduce(buildQueryStr, '')}`} />}
        {isSucceeded && <link rel="next" href={`${base}${nextList.reduce(buildQueryStr, '')}`} />}
      </Helmet>
    </>
  );
};

export default memo(SearchMeta, areEqual);
